import { useContext, useEffect, useState } from 'react'
import Link from 'next/link'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import Image from 'next/legacy/image'
import styles from './../../../components/_header.module.scss'
import classnames from 'classnames'
import { BlockScrollContext } from '../../_app'
import { ModalsContext } from '../../../utils/modals'
import { useAuth } from '../../../utils/auth'
import Avatar from '../../../components/Avatar'
import { t } from '../../../utils'
import { StoreLogo } from './StoreLogo'
import HeaderDropdown from './HeaderDropdown/HeaderDropdown'
import HeaderDropdownItem from './HeaderDropdown/HeaderDropdownItem'
import HeaderDropdownTrigger from './HeaderDropdown/HeaderDropdownTrigger'
import multitrackImg from '@public/images/store/multitrack.svg'
import patchesImg from '@public/images/store/patches.svg'
import vocalTracksImg from '@public/images/store/vocaltracks.svg'
import cartIconImg from '@public/images/store/cart.svg'
import downloadsIconImg from '@public/images/store/downloads.svg'
import StoreHeaderSearch from './StoreHeaderSearch'
import { songsPath } from '../../../utils/routeHelpers'
import { useLazyQuery } from '@apollo/client'
import { STORE_SHOPPING_CART_QUERY } from '../../../gql/queries/store_shopping_cart_query'
import { useRouter } from 'next/router'
import Announcements from '../../../components/Announcements'

const StoreHeader: React.FC = () => {
  const { currentUser } = useAuth()
  const router = useRouter()

  const [isMenuOpen, toggleMenu] = useState(false)
  const { setBlockScroll } = useContext(BlockScrollContext)
  const { openModal, closeModal } = useContext(ModalsContext)
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  const openSignInModal = () => {
    openModal({
      name: 'SignIn',
      props: {
        onClose: closeModal
      },
      modalTitle: t('Sign In')
    })
  }

  const [fetchCart, { data: cartData }] = useLazyQuery(
    STORE_SHOPPING_CART_QUERY,
    {
      fetchPolicy: 'cache-first'
    }
  )

  useEffect(() => {
    currentUser && fetchCart()
  }, [currentUser])

  const cartItemsNumber = cartData?.storeShoppingCart?.storeItems.length

  return (
    <header role="banner" className={`${styles.header}`}>
      <button
        className={styles.hamburger}
        type="button"
        onClick={() => {
          toggleMenu(!isMenuOpen)
          setBlockScroll(!isMenuOpen)
        }}
      >
        {isMenuOpen ? (
          <FontAwesomeIcon icon={faTimes} />
        ) : (
          <FontAwesomeIcon icon={faBars} />
        )}
      </button>

      <Link href={'/store'} prefetch={false} className={styles.logo}>
        <StoreLogo width={220} height={69} theme={'dark'} />
      </Link>

      <>
        <div
          className={classnames(
            isMenuOpen ? styles.menuOpen : styles.menuClosed,
            styles.storeHeaderMenuContainer
          )}
        >
          <nav className={styles.nav} role="nav" aria-label="main navigation">
            <ul>
              <li>
                <HeaderDropdown>
                  <HeaderDropdownTrigger>
                    <button
                      className={classnames({
                        [styles.active]:
                          router.route.includes('/store/products')
                      })}
                    >
                      Products
                    </button>
                  </HeaderDropdownTrigger>
                  <HeaderDropdownItem>
                    <Link
                      href="/store/products/multi_tracks"
                      prefetch={false}
                      className="columns p-2 is-vcentered is-mobile"
                      onClick={() => setBlockScroll(!isMenuOpen)}
                    >
                      <div className="column p-0 is-4">
                        <Image src={multitrackImg} alt="Multi Tracks Icon" />
                      </div>
                      <div className="column p-0 has-text-left">
                        {t('Multi Tracks')}
                      </div>
                    </Link>
                  </HeaderDropdownItem>
                  <HeaderDropdownItem>
                    <Link
                      href="/store/products/vocal_tracks"
                      prefetch={false}
                      className="columns p-2 is-vcentered is-mobile"
                      onClick={() => setBlockScroll(!isMenuOpen)}
                    >
                      <div className="column p-0 is-4">
                        <Image src={vocalTracksImg} alt="Vocal Tracks Icon" />
                      </div>
                      <div className="column p-0 has-text-left">
                        {t('Vocal Tracks')}
                      </div>
                    </Link>
                  </HeaderDropdownItem>
                  <HeaderDropdownItem>
                    <Link
                      href="/store/products/premium_patches"
                      prefetch={false}
                      className="columns p-2 is-vcentered is-mobile"
                      onClick={() => setBlockScroll(!isMenuOpen)}
                    >
                      <div className="column p-0 is-4">
                        <Image src={patchesImg} alt="Patches Icon" />
                      </div>
                      <div className="column p-0 has-text-left">
                        {t('Premium Patches')}
                      </div>
                    </Link>
                  </HeaderDropdownItem>
                </HeaderDropdown>
              </li>
              <li>
                <Link
                  href="/store/songs"
                  prefetch={false}
                  onClick={() => setBlockScroll(!isMenuOpen)}
                  className={classnames({
                    [styles.active]: router.route.includes('/store/songs')
                  })}
                >
                  {t('Songs')}
                </Link>
              </li>
              <li>
                <Link
                  href="https://help.worshiponline.com"
                  prefetch={false}
                  onClick={() => setBlockScroll(!isMenuOpen)}
                >
                  {t('Help Center')}
                </Link>
              </li>
              <li>
                <Link
                  href={songsPath()}
                  prefetch={false}
                  onClick={() => setBlockScroll(!isMenuOpen)}
                >
                  {t('Tutorials')}
                </Link>
              </li>
            </ul>
          </nav>

          <StoreHeaderSearch />

          {currentUser && (
            <ul className={styles.storeHeaderMenu}>
              <li>
                <Link href={'/store/cart'} prefetch={false}>
                  <span
                    className={classnames(styles.cartIcon, {
                      [styles.cartIconEmpty]: !cartItemsNumber
                    })}
                  >
                    <Image src={cartIconImg} alt="Cart Icon" />
                    <span className={styles.cartCircle}>{cartItemsNumber}</span>
                  </span>
                  <span className={styles.storeHeaderMenuLinkText}>
                    {t('Cart')}
                  </span>
                </Link>
              </li>
              <li>
                <Link href={'/store/downloads'} prefetch={false}>
                  <Image src={downloadsIconImg} alt="Downloads Icon" />
                  <span className={styles.storeHeaderMenuLinkText}>
                    {t('Downloads')}
                  </span>
                </Link>
              </li>
            </ul>
          )}

          {currentUser ? (
            <div className={styles.avatarContainer}>
              <div className="is-hidden-touch">
                <Announcements />
              </div>
              <Avatar />
            </div>
          ) : (
            <div className={styles.signoutOptions}>
              <button className="button is-regular" onClick={openSignInModal}>
                {t('Sign In')}
              </button>
            </div>
          )}
        </div>
        <div
          className={classnames(styles.mobileSearch, styles.storeMobileSearch)}
        >
          <div
            className={styles.searchIcon}
            onClick={() => {
              setIsSearchOpen(true)
            }}
          >
            <span className="icon is-small is-left">
              <FontAwesomeIcon icon={faSearch} />
            </span>
          </div>
          <div
            className={classnames(styles.searchContainer, {
              [styles.searchContainerOpen]: isSearchOpen
            })}
          >
            <StoreHeaderSearch />
            <div
              className={styles.closeSearch}
              onClick={() => {
                setIsSearchOpen(false)
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </div>
          </div>
        </div>
      </>
      {currentUser && (
        <div className="is-hidden-desktop">
          <Announcements />
        </div>
      )}
    </header>
  )
}

export default StoreHeader
